import { TechWorkingHoursModel } from "../models/TechWorkingHoursModel"

// Shared technician working-hours availability logic, used by both the
// working-hours drawer and the compact availability indicator on the job form.

const DAY_MS = 24 * 60 * 60 * 1000

export interface TechAvailability {
    // Technician has no working-hours restriction.
    alwaysAvailable: boolean
    // Technician's local "now" (its UTC fields represent the local wall clock).
    // Undefined when the timezone offset couldn't be resolved.
    techDate?: Date
    // Whether the technician is within a working window right now.
    // Undefined when it can't be determined (no resolvable timezone).
    openNow?: boolean
    // Live seconds until the next state change: until the shift ends when open,
    // until the next shift starts when closed. Undefined when unknown.
    secondsUntil?: number
}

const toMinutes = (t?: string): number | null => {
    if (!t) return null
    const parts = t.split(":")
    const h = Number(parts[0]), m = Number(parts[1] ?? 0)
    if (isNaN(h)) return null
    return h * 60 + (isNaN(m) ? 0 : m)
}

export function getTechAvailability(model: TechWorkingHoursModel | undefined, now: Date): TechAvailability {
    if (!model) return { alwaysAvailable: false }

    const hasHours = !!(model.workingHours && model.workingHours.length > 0)
    if (model.alwaysAvailable || !hasHours) return { alwaysAvailable: true, openNow: true }

    if (model.utcOffsetMinutes == null) return { alwaysAvailable: false }

    const techDate = new Date(now.getTime() + model.utcOffsetMinutes * 60000)
    const techDay = techDate.getUTCDay()
    const techMinutes = techDate.getUTCHours() * 60 + techDate.getUTCMinutes()

    const findItem = (day: number) => model.workingHours?.find(w => w.dayOfWeek == day)

    const today = findItem(techDay)
    let openNow = false
    if (today) {
        const from = toMinutes(today.fromTime), to = toMinutes(today.toTime)
        if (from != null && to != null) {
            openNow = to > from
                ? (techMinutes >= from && techMinutes < to)
                : (techMinutes >= from || techMinutes < to) // overnight span
        }
    }

    const startOfToday = new Date(techDate)
    startOfToday.setUTCHours(0, 0, 0, 0)

    let secondsUntil: number | undefined
    if (openNow) {
        const from = today ? toMinutes(today.fromTime) : null
        const to = today ? toMinutes(today.toTime) : null
        if (to != null) {
            let endMs = startOfToday.getTime() + to * 60000
            if (from != null && to <= from) endMs += DAY_MS // overnight shift ends next day
            secondsUntil = Math.max(0, Math.floor((endMs - techDate.getTime()) / 1000))
        }
    } else {
        for (let offset = 0; offset <= 7; offset++) {
            const item = findItem((techDay + offset) % 7)
            const from = item ? toMinutes(item.fromTime) : null
            if (from == null) continue
            const openMs = startOfToday.getTime() + offset * DAY_MS + from * 60000
            if (openMs > techDate.getTime()) {
                secondsUntil = Math.floor((openMs - techDate.getTime()) / 1000)
                break
            }
        }
    }

    return { alwaysAvailable: false, techDate, openNow, secondsUntil }
}

export function formatDuration(secs: number): string {
    const pad = (n: number) => n.toString().padStart(2, "0")
    const d = Math.floor(secs / 86400)
    const h = Math.floor((secs % 86400) / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (d > 0) return `${d}d ${h}h ${m}m`
    return `${pad(h)}:${pad(m)}:${pad(s)}`
}
